/**
 * TanStack Query hooks for the user's own gateway API keys (FEAT-033).
 *
 * Covers: listMine, usageMine (queries); createMine, rotateMine, revokeMine (mutations).
 * All mutations invalidate myApiKeyKeys.all on success.
 *
 * ---------------------------------------------------------------------------
 * SECRET RULE (read before touching this file)
 * ---------------------------------------------------------------------------
 * createMine and rotateMine return {key, secret}. The secret is shown ONCE and must NEVER
 * enter the TanStack Query cache nor any global or persistent state (Zustand, localStorage,
 * sessionStorage). Only the component that opened the reveal modal keeps it, in local
 * state, and drops it on close/unmount.
 *
 * A plain useMutation would break this rule: TanStack keeps the mutationFn result in the
 * MutationCache (mutation.state.data) while the hook is mounted and for gcTime after that.
 * So useCreateMyApiKey / useRotateMyApiKey run a mutation whose result is ONLY {key}; the
 * secret travels out of the mutationFn through a per-call callback straight to the promise
 * returned by mutateAsync (and to mutate's onSuccess). Neither the mutation's data nor its
 * variables ever hold the secret. Covered by hooks/__tests__/useMyApiKeys.test.ts.
 */

import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  apiKeysApi,
  type ApiKeyPublic,
  type ApiKeyUsageRange,
  type ApiKeyWithSecret,
  type CreateApiKeyInput,
} from '@/services/api/apiKeys';

// ---------------------------------------------------------------------------
// Query Keys
// ---------------------------------------------------------------------------

export const myApiKeyKeys = {
  all: ['api-keys', 'me'] as const,
  list: () => [...myApiKeyKeys.all, 'list'] as const,
  usage: (id: string, range: ApiKeyUsageRange) =>
    [...myApiKeyKeys.all, 'usage', id, range] as const,
};

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

/**
 * GET /me/api-keys -> { data: ApiKeyPublic[], policy }.
 * `enabled` lets a modal fetch only while open (design.md D.2).
 */
export function useMyApiKeys(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: myApiKeyKeys.list(),
    queryFn: () => apiKeysApi.listMine(),
    staleTime: 30_000,
    enabled: options.enabled ?? true,
  });
}

/** GET /me/api-keys/:id/usage?range= -- runs only with an id. */
export function useMyApiKeyUsage(id: string | null | undefined, range: ApiKeyUsageRange = '30d') {
  return useQuery({
    queryKey: myApiKeyKeys.usage(id ?? '', range),
    queryFn: () => apiKeysApi.usageMine(id as string, range),
    enabled: Boolean(id),
    staleTime: 60_000,
  });
}

// ---------------------------------------------------------------------------
// Secret-bearing mutations (create / rotate)
// ---------------------------------------------------------------------------

/** Internal variables: the caller's input plus the channel that carries the secret out. */
interface SecretMutationVars<TInput> {
  input: TInput;
  deliverSecret: (secret: string) => void;
}

export interface SecretMutationCallbacks {
  /** Receives {key, secret}. Put the secret in component-local state only. */
  onSuccess?: (result: ApiKeyWithSecret) => void;
  onError?: (error: Error) => void;
  onSettled?: () => void;
}

export interface UseSecretMutationResult<TInput> {
  /** Resolves with {key, secret}; rejects with the ApiClientError of the request. */
  mutateAsync: (input: TInput) => Promise<ApiKeyWithSecret>;
  /** Fire-and-forget variant; never throws (errors go to onError and to `error`). */
  mutate: (input: TInput, callbacks?: SecretMutationCallbacks) => void;
  isPending: boolean;
  isSuccess: boolean;
  isError: boolean;
  error: Error | null;
  /** The key returned by the last successful call -- WITHOUT the secret. */
  data: ApiKeyPublic | undefined;
  reset: () => void;
}

// --- FEAT-056 TASK-006 (export) ---
export function useSecretMutation<TInput>(
  request: (input: TInput) => Promise<ApiKeyWithSecret>,
): UseSecretMutationResult<TInput> {
  const qc = useQueryClient();

  const mutation = useMutation<ApiKeyPublic, Error, SecretMutationVars<TInput>>({
    mutationFn: async ({ input, deliverSecret }) => {
      const { key, secret } = await request(input);
      deliverSecret(secret);
      // Only the public key goes back to TanStack (mutation.state.data).
      return key;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: myApiKeyKeys.all });
    },
  });

  const { mutateAsync: runMutation } = mutation;

  const mutateAsync = useCallback(
    async (input: TInput): Promise<ApiKeyWithSecret> => {
      // Lives only in this call's closure; never stored by TanStack.
      const holder: { secret: string | null } = { secret: null };
      const key = await runMutation({
        input,
        deliverSecret: (s) => {
          holder.secret = s;
        },
      });
      const { secret } = holder;
      // deliverSecret stays in mutation.state.variables: clear what its closure can reach.
      holder.secret = null;
      if (typeof secret !== 'string' || secret === '') {
        // The API contract always returns the secret on 200/201; defensive only.
        throw new Error('API key secret missing from response');
      }
      return { key, secret };
    },
    [runMutation],
  );

  const mutate = useCallback(
    (input: TInput, callbacks?: SecretMutationCallbacks): void => {
      mutateAsync(input).then(
        (result) => {
          callbacks?.onSuccess?.(result);
          callbacks?.onSettled?.();
        },
        (error: unknown) => {
          callbacks?.onError?.(error instanceof Error ? error : new Error(String(error)));
          callbacks?.onSettled?.();
        },
      );
    },
    [mutateAsync],
  );

  return {
    mutateAsync,
    mutate,
    isPending: mutation.isPending,
    isSuccess: mutation.isSuccess,
    isError: mutation.isError,
    error: mutation.error,
    data: mutation.data,
    reset: mutation.reset,
  };
}

/**
 * POST /me/api-keys {name}. Without an active default key the API creates the DEFAULT one
 * (self-heal); otherwise an additional key under the org policy.
 * Returns {key, secret} for the screen to show ONCE -- see SECRET RULE at the top.
 */
export function useCreateMyApiKey(): UseSecretMutationResult<CreateApiKeyInput> {
  return useSecretMutation<CreateApiKeyInput>((input) => apiKeysApi.createMine(input));
}

/**
 * POST /me/api-keys/:id/rotate. The old secret stops working immediately.
 * Returns {key, secret} for the screen to show ONCE -- see SECRET RULE at the top.
 */
export function useRotateMyApiKey(): UseSecretMutationResult<string> {
  return useSecretMutation<string>((id) => apiKeysApi.rotateMine(id));
}

// ---------------------------------------------------------------------------
// Revoke
// ---------------------------------------------------------------------------

/** DELETE /me/api-keys/:id -- additional keys only (409 E_API_KEY_DEFAULT_PROTECTED otherwise). */
export function useRevokeMyApiKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiKeysApi.revokeMine(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: myApiKeyKeys.all });
    },
  });
}
